import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faToggleOn, faToggleOff, faTags, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { AuthGuard, EmailGuard } from "../App"; // Assuming AuthGuard and RoleGuard exist
import { StandardURL } from "../App"; // Import StandardURL from App.js

// --- Reusable Base Modal Component ---
const BaseModal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg transform transition-all duration-300 scale-100">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-3xl">&times;</button>
                </div>
                {children}
            </div>
        </div>
    );
};

// --- Add/Edit Category Modal Component ---
const AddEditCategoryModal = ({ isOpen, onClose, onSubmit, formData, handleChange, isEdit = false, selectedSetting, loading }) => {
    const isPersonal = formData.mpesacategory === 'personal';
    const isUpdate = isEdit && selectedSetting;

    const modalTitle = isUpdate ? `Update M-Pesa Category for Property ID: ${selectedSetting?.propertyId}` : "Add New M-Pesa Setting";
    const submitLabel = isUpdate ? "Update Category" : "Add Setting";

    return (
        <BaseModal isOpen={isOpen} onClose={onClose} title={modalTitle}>
            <form onSubmit={onSubmit}>
                <div className="p-6 grid grid-cols-1 gap-4">

                    {/* Property ID is only needed when adding a new setting */}
                    {!isUpdate && (
                        <input
                            type="text"
                            name="propertyId"
                            value={formData.propertyId || ''}
                            onChange={handleChange}
                            placeholder="Property ID"
                            className="border border-gray-300 rounded-lg p-3 focus:ring-blue-500 focus:border-blue-500"
                            required
                        />
                    )}
                    
                    {/* Category Selection */}
                    <select
                        name="mpesacategory"
                        value={formData.mpesacategory || 'common'}
                        onChange={handleChange}
                        className="border border-gray-300 rounded-lg p-3 focus:ring-blue-500 focus:border-blue-500"
                        required
                    >
                        <option value="common">Common</option>
                        <option value="personal">Personal</option>
                    </select>

                    {/* Conditional fields for Personal category */}
                    {isPersonal && (
                        <>
                            <input
                                type="text"
                                name="consumerkey"
                                value={formData.consumerkey || ''}
                                onChange={handleChange}
                                placeholder="Consumer Key"
                                className="border border-gray-300 rounded-lg p-3 focus:ring-green-500 focus:border-green-500"
                                required
                            />
                            <input
                                type="text"
                                name="businessshortcode"
                                value={formData.businessshortcode || ''}
                                onChange={handleChange}
                                placeholder="Business Short Code"
                                className="border border-gray-300 rounded-lg p-3 focus:ring-green-500 focus:border-green-500"
                                required
                            />
                            <input
                                type="text"
                                name="consumersecret"
                                value={formData.consumersecret || ''}
                                onChange={handleChange}
                                placeholder="Consumer Secret"
                                className="border border-gray-300 rounded-lg p-3 focus:ring-green-500 focus:border-green-500"
                                required
                            />
                            <input
                                type="text"
                                name="passkey"
                                value={formData.passkey || ''}
                                onChange={handleChange}
                                placeholder="Pass Key"
                                className="border border-gray-300 rounded-lg p-3 focus:ring-green-500 focus:border-green-500"
                                required
                            />
                        </>
                    )}
                </div>
                <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-xl">
                    <button type="button" onClick={onClose} className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition duration-200">Cancel</button>
                    <button type="submit" disabled={loading} className={`px-5 py-2 rounded-lg text-white font-semibold shadow-md transition duration-200 ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}>
                        {loading ? <FontAwesomeIcon icon={faSpinner} spin className="mr-2" /> : <FontAwesomeIcon icon={isUpdate ? faTags : faPlus} className="mr-2" />}
                        {submitLabel}
                    </button>
                </div>
            </form>
        </BaseModal>
    );
};


const MpesaIntroductionPage = () => {
    const [settings, setSettings] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingActionId, setLoadingActionId] = useState(null); // Used for specific row actions
    
    // Modals state
    const [isAddEditCategoryModalOpen, setIsAddEditCategoryModalOpen] = useState(false);

    const [selectedSetting, setSelectedSetting] = useState(null); // The property being acted on
    const [isEditCategoryMode, setIsEditCategoryMode] = useState(false); // Differentiates Add vs Category Update

    // Form data state (used by the modal)
    const initialFormState = {
        propertyId: '', // Only used for Add
        mpesacategory: 'common',
        consumerkey: '',
        businessshortcode: '',
        consumersecret: '',
        passkey: '',
    };
    const [formData, setFormData] = useState(initialFormState);

    const token = localStorage.getItem("token");
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };
    
    useEffect(() => {
        fetchSettings();
    }, []);

    // Helper for form changes
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const resetFormAndSelection = () => {
        setFormData(initialFormState);
        setSelectedSetting(null);
    };

    // --- API Calls ---

    // 1. FETCH ALL SETTINGS (GET: /iNyumba/admin/mpesaintroductioncontroller/viewall)
    const fetchSettings = async () => {
        if (!token) {
            toast.error("Authentication token not found.");
            return;
        }

        setIsLoading(true);
        try {
            const response = await axios.get(
                `${StandardURL}/iNyumba/admin/mpesaintroductioncontroller/viewall`,
                config
            );
            
            // Assuming the API response structure is response.data.response which is an array
            setSettings(Array.isArray(response.data.response) ? response.data.response : []);
            toast.success("M-Pesa settings fetched successfully!");
        } catch (error) {
            console.error("Error fetching M-Pesa settings:", error);
            toast.error(error.response?.data?.response || "Failed to fetch M-Pesa settings.");
            setSettings([]);
        } finally {
            setIsLoading(false);
        }
    };

    // 2. TOGGLE STATUS (PATCH: /iNyumba/admin/mpesaintroductioncontroller/status/{propertyId})
    const handleToggleStatus = async (setting) => {
        const propertyId = setting.propertyId;
        if (!token || !propertyId) return;

        // Backend determines the status change, we just confirm the action
        const newStatusText = setting.status ? "Deactivate" : "Activate";
        const isConfirmed = window.confirm(
            `Are you sure you want to ${newStatusText} M-Pesa for Property ID: ${propertyId}?`
        );

        if (!isConfirmed) return;

        setLoadingActionId(propertyId);

        try {
            await axios.patch(
                `${StandardURL}/iNyumba/admin/mpesaintroductioncontroller/status/${propertyId}`,
                {},
                config
            );
            toast.success(`M-Pesa status for ${propertyId} updated successfully!`);
            fetchSettings(); // Refresh the list
        } catch (error) {
            console.error("Error updating M-Pesa status:", error);
            toast.error(error.response?.data?.response || "Failed to update M-Pesa status.");
        } finally {
            setLoadingActionId(null);
        }
    };

    // 3 & 4. ADD SETTING (POST: /add/{propertyId}) / UPDATE CATEGORY (PATCH: /category/update/{propertyId})
    const handleAddOrUpdateCategory = async (e) => {
        e.preventDefault();
        
        const propertyId = isEditCategoryMode ? selectedSetting?.propertyId : formData.propertyId;
        if (!token || !propertyId) return;

        setLoadingActionId(propertyId);
        
        const isPersonal = formData.mpesacategory === 'personal';
        let payload = {
            mpesacategory: formData.mpesacategory,
        };

        if (isPersonal) {
            payload = {
                ...payload,
                consumerkey: formData.consumerkey,
                businessshortcode: formData.businessshortcode,
                consumersecret: formData.consumersecret,
                passkey: formData.passkey,
            };
        }
        
        try {
            let url = '';
            let method = '';
            let successMessage = '';
            
            if (isEditCategoryMode) {
                url = `${StandardURL}/iNyumba/admin/mpesaintroductioncontroller/category/update/${propertyId}`;
                method = 'patch';
                successMessage = `Category for ${propertyId} updated to ${formData.mpesacategory.toUpperCase()} successfully!`;
            } else {
                url = `${StandardURL}/iNyumba/admin/mpesaintroductioncontroller/add/${propertyId}`;
                method = 'post';
                successMessage = `New M-Pesa setting added for Property ID: ${propertyId}!`;
            }

            if (method === 'post') {
                await axios.post(url, payload, config);
            } else if (method === 'patch') {
                await axios.patch(url, payload, config);
            }

            toast.success(successMessage);
            setIsAddEditCategoryModalOpen(false);
            setIsEditCategoryMode(false);
            resetFormAndSelection();
            fetchSettings();
        } catch (error) {
            console.error(`Error ${isEditCategoryMode ? 'updating category' : 'adding setting'}:`, error);
            toast.error(error.response?.data?.response || `Failed to ${isEditCategoryMode ? 'update category' : 'add setting'}.`);
        } finally {
            setLoadingActionId(null);
        }
    };

    // --- Open Modal Functions ---
    const openModal = (setting = null, isEditMode = false) => {
        setSelectedSetting(setting);
        setIsEditCategoryMode(isEditMode);
        
        let newFormData = { ...initialFormState };
        if (setting && isEditMode) {
            // Pre-populate fields for Category Update
            newFormData.mpesacategory = setting.mpesacategory || 'common';
            newFormData.consumerkey = setting.consumerkey || '';
            newFormData.businessshortcode = setting.businessshortcode || '';
            newFormData.consumersecret = setting.consumersecret || '';
            newFormData.passkey = setting.passkey || '';
        } 

        setFormData(newFormData);
        setIsAddEditCategoryModalOpen(true);
    };

    return (
        <EmailGuard>
        <AuthGuard>
            <div className="container mx-auto px-4 py-8">
                
                <h1 className="text-4xl font-extrabold text-gray-900 mb-8 border-b-4 border-green-600 pb-2">
                    M-Pesa Settings Management
                </h1>

                {/* Header and Add Button */}
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold text-gray-700">All M-Pesa Settings</h2>
                    <button
                        onClick={() => openModal(null, false)} // Open Add Mode
                        className="flex items-center bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg transition duration-300 transform hover:scale-[1.02]"
                    >
                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                        Add New Setting
                    </button>
                </div>

                {/* Settings Table */}
                <div className="bg-white shadow-2xl rounded-xl overflow-hidden">
                    {isLoading ? (
                        <div className="flex justify-center items-center py-12">
                            <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-green-600" />
                            <span className="ml-3 text-lg text-gray-600">Loading M-Pesa settings...</span>
                        </div>
                    ) : settings.length === 0 ? (
                        <div className="text-center py-10 text-gray-500 text-lg">
                            No M-Pesa settings found.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {["Property ID", "Category", "Short Code", "Consumer Key", "Consumer Secret", "Status", "Actions"].map((header) => (
                                            <th
                                                key={header}
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                            >
                                                {header}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {settings.map((setting) => (
                                        <tr key={setting.propertyId} className="hover:bg-gray-50 transition duration-150">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{setting.propertyId}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 capitalize">{setting.mpesacategory || 'N/A'}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{setting.businessshortcode || 'N/A'}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 max-w-xs truncate">{setting.consumerkey ? `${setting.consumerkey.substring(0, 8)}...` : 'N/A'}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 max-w-xs truncate">{setting.consumersecret ? `${setting.consumersecret.substring(0, 8)}...` : 'N/A'}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 max-w-xs truncate">{setting.status ? `${setting.status.substring(0, 8)}...` : 'N/A'}</td>
                        
                                            <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
                                                <div className="flex space-x-2">
                                                    {/* Toggle Status (PATCH) */}
                                                    <button
                                                        onClick={() => handleToggleStatus(setting)}
                                                        disabled={loadingActionId === setting.propertyId}
                                                        className={`p-2 rounded-full transition duration-150 disabled:opacity-50 ${
                                                            setting.status 
                                                                ? 'text-red-600 hover:text-red-900 hover:bg-red-50'
                                                                : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                                                        }`}
                                                        title={setting.status ? 'Deactivate' : 'Activate'}
                                                    >
                                                        {loadingActionId === setting.propertyId ? <FontAwesomeIcon icon={faSpinner} spin /> : (
                                                            <FontAwesomeIcon icon={setting.status ? faToggleOn : faToggleOff} />
                                                        )}
                                                    </button>
                                                    
                                                    {/* Update Category (PATCH) */}
                                                    <button
                                                        onClick={() => openModal(setting, true)} // Open Edit/Update Category Mode
                                                        className="text-orange-600 hover:text-orange-900 p-2 rounded-full hover:bg-orange-50 transition duration-150"
                                                        title="Update Category"
                                                    >
                                                        <FontAwesomeIcon icon={faTags} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Category Modal */}
            <AddEditCategoryModal
                isOpen={isAddEditCategoryModalOpen}
                onClose={() => {setIsAddEditCategoryModalOpen(false); setIsEditCategoryMode(false); resetFormAndSelection();}}
                onSubmit={handleAddOrUpdateCategory}
                formData={formData}
                handleChange={handleChange}
                isEdit={isEditCategoryMode}
                selectedSetting={selectedSetting}
                loading={loadingActionId}
            />

        </AuthGuard>
        </EmailGuard>
    );
};

export default MpesaIntroductionPage;